import { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import Layout, { isAdminRole } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Page, Card, EmptyState, StatusPill, DatePicker, TH, TD } from "@/components/polaris";
import { Pill } from "@/components/AnswerBits";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UploadCloud, FileText, Power, PowerOff, RefreshCw, Layers, Eye, Trash2, Pencil, ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

export default function Library() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const canReview = isAdmin || user?.role === "reviewer";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  const toggleLender = (l) => setExpanded((s) => {
    const n = new Set(s);
    n.has(l) ? n.delete(l) : n.add(l);
    return n;
  });

  // Group documents by lender (alphabetical); documents within a group keep the
  // reverse-chronological order returned by the API.
  const groups = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const k = d.lender || "Unspecified";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([lender, items]) => ({ lender, items }));
  }, [docs]);

  const load = () => {
    setLoading(true);
    api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (fn, vid, msg = "Updated") => {
    try {
      await fn(vid);
      toast.success(msg);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout>
      <Page title="Document library" subtitle="Guideline packages, versions, and indexing status."
        actions={isAdmin && <UploadDialog docs={docs} onDone={load} />}>
        {loading && docs.length === 0 && <div className="text-sm text-[#6D7175]">Loading…</div>}
        {!loading && docs.length === 0 && (
          <EmptyState icon={FileText} data-testid="empty-library">
            No documents yet. {isAdmin ? "Upload a guideline PDF to begin." : "Ask an admin to upload guidelines."}
          </EmptyState>
        )}

        <div className="space-y-3">
          {groups.map(({ lender, items }) => {
            const isOpen = expanded.has(lender);
            return (
              <Card key={lender} data-testid={`lender-group-${lender}`}>
                <button type="button" onClick={() => toggleLender(lender)}
                  className="w-full flex items-center gap-2.5 px-4 py-3.5 hover:bg-[#FAFAFB] text-left transition-colors"
                  data-testid={`lender-toggle-${lender}`}>
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-[#6D7175]" />
                          : <ChevronRight className="h-4 w-4 shrink-0 text-[#6D7175]" />}
                  <span className="font-semibold text-[#202223]">{lender}</span>
                  <Pill tone="neutral" className="whitespace-nowrap shrink-0">{items.length} doc{items.length !== 1 ? "s" : ""}</Pill>
                </button>
                {isOpen && (
                  <div className="border-t border-[#E1E3E5] overflow-x-auto">
                    <table className="w-full min-w-[560px] border-collapse [&_td]:px-3 [&_th]:px-3 sm:[&_td]:px-4 sm:[&_th]:px-4">
                      <thead>
                        <tr>
                          <th className={TH}>Version</th><th className={TH}>Status</th><th className={TH}>Source file</th><th className={TH}>Indexed</th>
                          {isAdmin && <th className={`${TH} text-right`}><span className="sr-only">Actions</span></th>}
                        </tr>
                      </thead>
                      {items.map((d) => (
                        <tbody key={d.id} data-testid={`document-${d.id}`}>
                          <tr className="bg-[#FAFAFB]">
                            <td colSpan={isAdmin ? 5 : 4} className="px-4 py-3 border-b border-[#E1E3E5]">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  <h3 className="font-semibold text-[15px] text-[#202223]">{d.title}</h3>
                                  <Pill tone="highlight">{d.doc_type}</Pill>
                                  {d.product_type && <Pill tone="neutral">{d.product_type}</Pill>}
                                </div>
                                {isAdmin && (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <EditDocumentDialog doc={d} docs={docs} onDone={load} />
                                    <UploadDialog docs={docs} onDone={load} attachTo={d}
                                      trigger={<Button variant="outline" size="sm" className="h-8 gap-1 rounded-lg bg-white border-[#C9CCCF] hover:bg-[#F1F2F4]" data-testid={`add-version-${d.id}`}><Layers className="h-3.5 w-3.5" /> New version</Button>} />
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                              {d.versions.map((v) => (
                                <tr key={v.id} className="hover:bg-[#FAFAFB] transition-colors" data-testid={`version-${v.id}`}>
                                  <td className={`${TD} whitespace-nowrap`}><span className="font-mono-cite text-[13px] text-[#005180] font-medium">{v.version_label}</span></td>
                                  <td className={`${TD} whitespace-nowrap`}>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <StatusPill status={v.status} />
                                      {v.is_active ? <Pill tone="success">Active</Pill> : <Pill tone="neutral">Superseded</Pill>}
                                    </div>
                                  </td>
                                  <td className={`${TD} max-w-[150px] lg:max-w-[260px]`}>
                                    <span className="block truncate text-xs text-[#45494D]" title={v.original_filename}>{v.original_filename}</span>
                                    <span className="block text-[11px] text-[#6D7175] mt-0.5" data-testid={`effective-${v.id}`}>{v.effective_date ? `Effective ${v.effective_date}` : "No effective date"}</span>
                                  </td>
                                  <td className={`${TD} text-xs text-[#6D7175]`}>
                                    {typeof v.chunk_count === "number" ? (
                                      <>
                                        <span className="block whitespace-nowrap">{v.chunk_count} chunk{v.chunk_count === 1 ? "" : "s"}</span>
                                        {v.source_format !== "markdown" && <span className="block whitespace-nowrap">{v.page_count} pages</span>}
                                      </>
                                    ) : "—"}
                                  </td>
                                  {isAdmin && (
                                    <td className={`${TD} text-right whitespace-nowrap w-[1%]`}>
                                      <VersionActions v={v} canReview={canReview} act={act} />
                                    </td>
                                  )}
                                </tr>
                              ))}
                        </tbody>
                      ))}
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </Page>
    </Layout>
  );
}

function VersionActions({ v, canReview, act }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <div className="inline-flex items-center gap-1">
      {canReview && <ChunkViewer versionId={v.id} />}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" aria-label="More actions" data-testid={`version-actions-${v.id}`}
            className="h-8 w-8 p-0 rounded-lg bg-white border-[#C9CCCF] hover:bg-[#F1F2F4]">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44 font-polaris">
          {v.is_active
            ? <DropdownMenuItem onSelect={() => act((id) => api.post(`/versions/${id}/deactivate`), v.id)} data-testid={`deactivate-${v.id}`}><PowerOff className="h-4 w-4 mr-2" /> Deactivate</DropdownMenuItem>
            : <DropdownMenuItem onSelect={() => act((id) => api.post(`/versions/${id}/activate`), v.id)} data-testid={`activate-${v.id}`}><Power className="h-4 w-4 mr-2" /> Activate</DropdownMenuItem>}
          <DropdownMenuItem onSelect={() => act((id) => api.post(`/versions/${id}/reprocess`), v.id)} data-testid={`reprocess-${v.id}`}><RefreshCw className="h-4 w-4 mr-2" /> Reprocess</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setConfirmDelete(true)} data-testid={`delete-version-${v.id}`}
            className="text-[#7A1408] focus:bg-[#FDE8E8] focus:text-[#7A1408]"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete version {v.version_label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes this version, its extracted chunks, and its ingestion history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => act((id) => api.delete(`/versions/${id}`), v.id, "Version deleted")} data-testid={`confirm-delete-version-${v.id}`}
              className="bg-[#D72C0D] text-white hover:bg-[#BC2200]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ChunkViewer({ versionId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open) api.get(`/versions/${versionId}/chunks?limit=100`).then((r) => setData(r.data)).catch(() => {});
  }, [open, versionId]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs rounded-lg text-[#303030] hover:bg-[#F1F2F4]" data-testid={`view-chunks-${versionId}`}><Eye className="h-3.5 w-3.5" /> Chunks</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Extracted chunks {data ? `(${data.total})` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {data?.chunks?.map((c) => (
            <div key={c.id} className="border border-[#E1E3E5] rounded-lg p-3 text-sm">
              <div className="font-mono-cite text-[11px] text-primary">{c.page_start == null ? "[Section]" : `[Pg ${c.page_start}–${c.page_end}]`} {(c.section_path || "").replace(/\*\*/g, "")}</div>
              <div className="flex gap-1 my-1.5">
                {c.content_type === "table" && <Pill tone="neutral">Table</Pill>}
                {c.appendix_flag && <Pill tone="neutral">Appendix</Pill>}
                {c.footnote_flag && <Pill tone="warning">Footnote</Pill>}
              </div>
              <p className="text-muted-foreground">{c.raw_text}</p>
            </div>
          ))}
          {data && data.chunks.length === 0 && <p className="text-sm text-muted-foreground">No chunks. The document may still be processing or failed.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDocumentDialog({ doc, docs, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: doc.title || "", lender: doc.lender || "",
    doc_type: doc.doc_type || "Guideline", product_type: doc.product_type || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Reset the form to the latest values whenever the dialog opens.
  useEffect(() => {
    if (open) setForm({
      title: doc.title || "", lender: doc.lender || "",
      doc_type: doc.doc_type || "Guideline", product_type: doc.product_type || "",
    });
  }, [open, doc]);

  const save = async () => {
    if (!form.title.trim() || !form.lender.trim() || !form.doc_type.trim())
      return toast.error("Title, lender and document type are required");
    setBusy(true);
    try {
      await api.put(`/documents/${doc.id}`, {
        title: form.title.trim(),
        lender: form.lender.trim(),
        doc_type: form.doc_type.trim(),
        product_type: form.product_type.trim() || null,
      });
      toast.success("Document details updated");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const existingLenders = [...new Set((docs || []).map((d) => d.lender).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const existingProducts = [...new Set((docs || []).map((d) => d.product_type).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1 rounded-lg bg-white border-[#C9CCCF] hover:bg-[#F1F2F4]" data-testid={`edit-document-${doc.id}`}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit document details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Document title" v={form.title} on={(x) => set("title", x)} tid="edit-title" />
          <CreatableSelect
            label="Lender / Investor"
            value={form.lender}
            onChange={(x) => set("lender", x)}
            existing={existingLenders}
            testidPrefix="edit-lender"
            placeholder="Select a lender / investor"
            addLabel="+ Add new lender / investor…"
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Document type</Label>
              <Select value={form.doc_type} onValueChange={(x) => set("doc_type", x)}>
                <SelectTrigger data-testid="edit-doctype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Guideline", "Overlay", "Matrix", "Bulletin", "Handbook", "Form"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <CreatableSelect
              label="Product type"
              value={form.product_type}
              onChange={(x) => set("product_type", x)}
              existing={existingProducts}
              testidPrefix="edit-product"
              placeholder="Select a product type"
              addLabel="+ Add new product type…"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Applies to all versions of this document and updates how it appears in search and cited evidence.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={busy} data-testid="edit-document-submit">{busy ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadDialog({ docs, onDone, attachTo, trigger }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("file"); // "file" | "paste"
  const [pasteText, setPasteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: attachTo?.title || "", lender: attachTo?.lender || "", doc_type: attachTo?.doc_type || "Guideline",
    product_type: attachTo?.product_type || "", effective_date: "", version_label: "v1",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "paste") {
        if (pasteText.trim().length < 20) { setBusy(false); return toast.error("Paste some guideline text first"); }
        await api.post("/documents/upload_text", {
          content: pasteText, ...form, document_id: attachTo?.id || "",
        });
      } else {
        if (!file) { setBusy(false); return toast.error("Choose a PDF, Markdown (.md) or text (.txt) file"); }
        const fd = new FormData();
        fd.append("file", file);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
        if (attachTo) fd.append("document_id", attachTo.id);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded. Ingestion started.");
      setOpen(false);
      setFile(null);
      setPasteText("");
      onDone();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button className="gap-2" data-testid="upload-button"><UploadCloud className="h-4 w-4" /> Upload guideline</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{attachTo ? `New version — ${attachTo.title}` : "Upload guideline"}</DialogTitle>
          <DialogDescription>Upload a PDF, Markdown (.md) or text file, or paste Markdown directly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-1 p-1 bg-[#F1F2F4] rounded-lg w-fit">
            <button type="button" onClick={() => setMode("file")} data-testid="upload-mode-file"
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${mode === "file" ? "bg-white shadow-sm font-semibold text-[#202223]" : "text-[#6D7175] hover:text-[#202223]"}`}>Upload file</button>
            <button type="button" onClick={() => setMode("paste")} data-testid="upload-mode-paste"
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${mode === "paste" ? "bg-white shadow-sm font-semibold text-[#202223]" : "text-[#6D7175] hover:text-[#202223]"}`}>Paste Markdown</button>
          </div>
          {mode === "file" ? (
            <div className="space-y-1.5">
              <Label>File (PDF, Markdown .md, or text .txt)</Label>
              <Input type="file" accept=".pdf,.md,.markdown,.txt,application/pdf,text/markdown,text/plain"
                onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input" />
              <p className="text-[11px] text-muted-foreground">Markdown/text ingests faster and cites by section heading (no page numbers).</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Paste guideline text (Markdown)</Label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                data-testid="upload-paste-input"
                placeholder={"# Cash-Out Refinances\n\n## Maximum LTV\nThe maximum LTV is 80 percent of the Adjusted Value.\n..."}
                className="w-full h-40 rounded-lg border border-input bg-white p-2.5 text-base sm:text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-[#1E7FC2]/30"
              />
              <p className="text-[11px] text-muted-foreground">Use <span className="font-mono">#</span>/<span className="font-mono">##</span> headings — they become the section path used for citations.</p>
            </div>
          )}
          {!attachTo && (
            <>
              <Field label="Document title" v={form.title} on={(x) => set("title", x)} tid="upload-title" />
              <CreatableSelect
                label="Lender / Investor"
                value={form.lender}
                onChange={(x) => set("lender", x)}
                existing={[...new Set((docs || []).map((d) => d.lender).filter(Boolean))].sort((a, b) => a.localeCompare(b))}
                testidPrefix="upload-lender"
                placeholder="Select a lender / investor"
                addLabel="+ Add new lender / investor…"
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Document type</Label>
                  <Select value={form.doc_type} onValueChange={(x) => set("doc_type", x)}>
                    <SelectTrigger data-testid="upload-doctype"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Guideline", "Overlay", "Matrix", "Bulletin", "Handbook", "Form"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <CreatableSelect
                  label="Product type"
                  value={form.product_type}
                  onChange={(x) => set("product_type", x)}
                  existing={[...new Set((docs || []).map((d) => d.product_type).filter(Boolean))].sort((a, b) => a.localeCompare(b))}
                  testidPrefix="upload-product"
                  placeholder="Select a product type"
                  addLabel="+ Add new product type…"
                />
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Version label" v={form.version_label} on={(x) => set("version_label", x)} tid="upload-version" />
            <div className="space-y-1.5">
              <Label>Effective date</Label>
              <DatePicker value={form.effective_date} onChange={(x) => set("effective_date", x)} testid="upload-effdate" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="upload-submit">{busy ? "Uploading…" : "Upload & ingest"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreatableSelect({ label, value, onChange, existing, testidPrefix, placeholder, addLabel }) {
  const NEW = "__new__";
  const [creating, setCreating] = useState(existing.length === 0 || (!!value && !existing.includes(value)));
  const hasExisting = existing.length > 0;
  useEffect(() => { if (hasExisting && !value) setCreating(false); }, [hasExisting]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {creating ? (
        <div className="space-y-1.5">
          <Input
            autoFocus
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testidPrefix}
          />
          {existing.length > 0 && (
            <button
              type="button"
              onClick={() => { onChange(""); setCreating(false); }}
              className="text-xs text-primary hover:underline"
              data-testid={`${testidPrefix}-choose-existing`}
            >
              ← Choose from existing
            </button>
          )}
        </div>
      ) : (
        <Select
          value={value || undefined}
          onValueChange={(v) => {
            if (v === NEW) { onChange(""); setCreating(true); }
            else onChange(v);
          }}
        >
          <SelectTrigger data-testid={`${testidPrefix}-select`}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {existing.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            <SelectItem value={NEW} className="text-primary font-medium" data-testid={`${testidPrefix}-add-new`}>
              {addLabel}
            </SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function Field({ label, v, on, tid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={v} onChange={(e) => on(e.target.value)} data-testid={tid} />
    </div>
  );
}
